import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CustomHeading from "../../../components/common/CustomHeading";
import CustomSearch from "../../../components/common/CustomSearch";
import CustomButton from "../../../components/common/CustomButton";
import CustomTable from "../../../components/common/CustomTable";
import CustomChip from "../../../components/common/CustomChip";
import { BiEdit } from "react-icons/bi";
import { MdFilterListAlt } from "react-icons/md";
import CustomSidebar from "../../../components/common/CustomSidebar";
import { validateInput } from "../../../utils/regexValidators";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/Constant/Notification";
import AdvocateService from "../../Service/AdvocateService";
import Header from "../../Header/Header";
import AdvocatesInfo from "./AdvocatesInfo";
import { userUid } from "../../../utils/storage";
import { ToastContainer } from "react-toastify";

const AdvocatesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [advocateList, setAdvocateList] = useState([]);
  const [advocateCount, setAdvocateCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageToken, setPageToken] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [prevPageToken, setPrevPageToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageTokens, setPageTokens] = useState([null]);
  const [cache] = useState(() => new Map());
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    status: null,
    location: null,
  });

  const [form, setForm] = useState({
    advocateId: "",
    name: "",
    email: "",
    phone: "",
    officeAddress: "",
  });

  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!form.name.trim()) {
      showErrorToast("Advocate name is required");
      return;
    }
    if (!form.email || !validateInput("email", form.email)) {
      showErrorToast("Please enter a valid email address");
      return;
    }
    if (!form.phone || !/^\d{10}$/.test(form.phone)) {
      showErrorToast("Phone number must be exactly 10 digits");
      return;
    }

    setLoading(true);

    try {
      const advocatePayload = {
        name: form.name.trim(),
        emailId: form.email.trim(),
        phoneNumber: form.phone.trim(),
        officeAddress: form.officeAddress.trim(),
        createdById: userUid,
        modifiedById: userUid,
      };

      let response;

      if (form.advocateId) {
        response = await AdvocateService.updateAdvocate(
          form.advocateId,
          advocatePayload
        );
      } else {
        response = await AdvocateService.addAdvocate(advocatePayload);
      }

      if (response.status === 200 || response.status === 201) {
        showSuccessToast(
          response.data.message ||
            `Advocate ${form.advocateId ? "updated" : "created"} successfully`
        );
        setIsModalOpen(false);
        setForm({
          advocateId: "",
          name: "",
          email: "",
          phone: "",
          officeAddress: "",
          specialization: "",
          status: true,
        });

        cache.clear();
        setCurrentPage(1);
        setPageToken(null);
        setPageTokens([null]);
        getAdvocateList({
          search: searchTerm,
          page: 1,
          pageSize: pageSize, // Ensure pageSize is passed
          status: selectedFilters.status,
          location: selectedFilters.location,
        });
      } 
    } catch (error) {
      console.error("Error saving advocate:", error);
      showErrorToast(
        error.response?.data?.message ||
          `Error ${form.advocateId ? "updating" : "creating"} advocate`
      );
    } finally {
      setLoading(false);
    }
  };

  const getCacheKey = (page, search, status, location, pageSize) =>
    `${page}_${search}_${status || "ALL"}_${location || "ALL"}_${pageSize}`;

  const getAdvocateList = async ({
    search = "",
    page = 1,
    direction = "",
    pageToken: customToken = null,
    status = null,
    location = null,
    pageSize = 10, // Include pageSize in parameters
  } = {}) => {
    const cacheKey = getCacheKey(page, search, status, location, pageSize);
    const tokenForPage = customToken ?? pageTokens[page - 1] ?? null;

    if (cache.has(cacheKey)) {
      const cachedData = cache.get(cacheKey);
      setAdvocateList(cachedData.advocates);
      setAdvocateCount(cachedData.maxCount);
      setHasMore(cachedData.hasMore);
      setNextPageToken(cachedData.nextPageToken);
      setPrevPageToken(cachedData.prevPageToken);
      setCurrentPage(page);
      setPageToken(tokenForPage);
      return;
    }

    try {
      setLoading(true);

      const body = {
        limit: pageSize,
        searchKey: search,
        startAfter: direction === "next" ? tokenForPage : null,
      };

      const response = await AdvocateService.getAdvocates(body);
      const advocates = response.data.advocates || [];
      const maxCount = response.data.maxCount || 0;
      const nextToken = response.data?.pagination?.lastDocId || null;
      const prevToken = response.data?.pagination?.firstDocId || null;
      const calculatedHasMore = response.data?.pagination?.hasMore || false;

      const cacheData = {
        advocates,
        maxCount,
        hasMore: calculatedHasMore,
        nextPageToken: nextToken,
        prevPageToken: prevToken,
      };

      cache.set(cacheKey, cacheData);

      if (direction === "next" && nextToken && calculatedHasMore) {
        setPageTokens((prev) => {
          const newTokens = [...prev];
          if (newTokens.length === page) {
            newTokens.push(nextToken);
          }
          return newTokens;
        });
      } else if (direction === "previous" && prevToken && page > 1) {
        setPageTokens((prev) => {
          const newTokens = [...prev];
          if (newTokens.length >= page && newTokens[page - 1] !== prevToken) {
            newTokens[page - 1] = prevToken;
          }
          return newTokens;
        });
      }

      setAdvocateList(advocates);
      setAdvocateCount(maxCount);
      setHasMore(calculatedHasMore);
      setNextPageToken(nextToken);
      setPrevPageToken(prevToken);
      setPageToken(direction === "next" ? nextToken : prevToken);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching advocate list:", error);
      setHasMore(false);
      showErrorToast("Failed to fetch advocate list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cache.clear();
    setCurrentPage(1);
    setPageToken(null);
    setPageTokens([null]);
    getAdvocateList({
      search: "",
      page: 1,
      pageSize: pageSize,
      status: null,
      location: null,
    });
  }, [pageSize]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    cache.clear();
    setCurrentPage(1);
    setPageToken(null);
    setPageTokens([null]);
    getAdvocateList({
      search: searchTerm,
      page: 1,
      pageSize: pageSize,
      status: selectedFilters.status,
      location: selectedFilters.location,
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (token, direction) => {
    if (direction === "next" && hasMore) {
      const newPage = currentPage + 1;
      getAdvocateList({
        search: searchTerm,
        pageToken: nextPageToken,
        page: newPage,
        direction: "next",
        pageSize: pageSize,
        status: selectedFilters.status,
        location: selectedFilters.location,
      });
    } else if (direction === "previous" && currentPage > 1) {
      const newPage = currentPage - 1;
      const prevToken = pageTokens[newPage - 1] || prevPageToken;
      getAdvocateList({
        search: searchTerm,
        pageToken: prevToken,
        page: newPage,
        direction: "prev",
        pageSize: pageSize,
        status: selectedFilters.status,
        location: selectedFilters.location,
      });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  const handleRemove = (filterType) => {
    const newFilters = { ...selectedFilters, [filterType]: null };
    setSelectedFilters(newFilters);
    cache.clear();
    setCurrentPage(1);
    setPageToken(null);
    setPageTokens([null]);
    getAdvocateList({
      search: searchTerm,
      page: 1,
      pageSize: pageSize,
      status: newFilters.status,
      location: newFilters.location,
    });
  };

  const columns = [
    {
      key: "name",
      title: "Name",
      render: (_, record) => (
        <div className="capitalize truncate cursor-pointer text-center">
          <div className="flex flex-col gap-2">
            <div className="capitalize font-medium">{record?.name}</div>
          </div>
        </div>
      ),
    },
    {
      key: "phoneNumber",
      title: "Phone Number",
      render: (_, record) => (
        <div className="truncate">{record?.phoneNumber ?? "N/A"}</div>
      ),
    },
    {
      key: "emailId",
      title: "Email",
      render: (_, record) => (
        <div
          className="truncate line-clamp-2 text-wrap"
          title={record?.emailId}
        >
          {record?.emailId ?? "N/A"}
        </div>
      ),
    },
    {
      key: "action",
      title: "Action",
      render: (_, record) => (
        <div
          onClick={() => {
            setForm({
              advocateId: record.id || "",
              name: record.name || "",
              email: record.emailId || "",
              phone: record.phoneNumber || "",
              officeAddress: record.officeAddress || "",
            });
            setIsModalOpen(true);
          }}
          className="flex justify-center items-center h-full cursor-pointer"
        >
          <BiEdit className="text-blue-500 text-lg" />
        </div>
      ),
    },
  ];

  return (
    <div className="bg-appGrayBackground pb-7">
      <Header />
      <ToastContainer />
      <div className="pt-28 px-6">
        <div className="flex items-center justify-between">
          <CustomHeading title={<>Advocates</>} isIcon={true} />
          <div className="flex items-center gap-4">
            <CustomSearch
              placeholder={"Search Advocate"}
              searchTerm={searchTerm}
              handleSearchSubmit={handleSearchSubmit}
              handleSearchChange={handleSearchChange}
            />
            <CustomButton
              label={"Create Advocate"}
              onClick={() => {
                setForm({
                  advocateId: "",
                  name: "",
                  email: "",
                  phone: "",
                  officeAddress: "",
                });
                setIsModalOpen(true);
              }}
            />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4">
          {selectedFilters.status !== null && (
            <CustomChip
              label={`Status: ${selectedFilters.status ? "Active" : "Inactive"}`}
              onRemove={() => handleRemove("status")}
            />
          )}
          {selectedFilters.location && (
            <CustomChip
              label={`Location: ${selectedFilters.location.label}`}
              onRemove={() => handleRemove("location")}
            />
          )}
        </div>

        <div className="w-full mt-[20px]">
          <CustomTable
            columns={columns}
            data={advocateList}
            isPagination={true}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            currentPage={currentPage}
            nextId={nextPageToken}
            prevId={prevPageToken}
            hasMore={hasMore}
            loading={loading}
          />
        </div>

        <AdvocatesInfo
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          form={form}
          setForm={setForm}
          handleSubmit={handleSubmit}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default AdvocatesPage;